import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Link
} from '@mui/material';
import {
  Lock as LockIcon
} from '@mui/icons-material';

interface TOTPLoginProps {
  onSuccess?: (token: string) => void;
  onError?: (error: string) => void;
  disabled?: boolean;
}

function TOTPLogin({ onSuccess, onError, disabled = false }: TOTPLoginProps) {
  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState('');
  const [useBackupCode, setUseBackupCode] = useState(false);

  const handleVerify = async () => {
    if (!code || (useBackupCode ? code.length < 8 : code.length !== 6)) {
      onError?.('Please enter a valid code');
      return;
    }

    setLoading(true);
    
    try {
      // The TOTP validation happens in the parent component (Landing.tsx)
      // This component just collects the code and passes it back
      onSuccess?.(code);
    } catch (error: any) {
      console.error('TOTP verification error:', error);
      onError?.('Failed to verify code');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (useBackupCode) {
      // Allow alphanumeric and hyphen for backup codes (XXXX-XXXX format)
      setCode(value.toUpperCase().replace(/[^A-Z0-9-]/g, '').slice(0, 9));
    } else {
      // Only digits for TOTP codes
      setCode(value.replace(/\D/g, '').slice(0, 6));
    }
  };

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <LockIcon sx={{ fontSize: 48, color: '#6366f1', mb: 2 }} />
        <Typography variant="h5" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.95)' }}>
          Enter Verification Code
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          {useBackupCode 
            ? 'Enter one of your backup codes' 
            : 'Open your authenticator app and enter the 6-digit code'}
        </Typography>
      </Box>

      <TextField
        fullWidth
        value={code}
        onChange={handleCodeChange}
        placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
        disabled={loading || disabled}
        autoFocus
        inputProps={{ 
          maxLength: useBackupCode ? 9 : 6,
          style: { 
            textAlign: 'center', 
            fontSize: '24px', 
            letterSpacing: useBackupCode ? '2px' : '8px',
            fontFamily: 'monospace'
          }
        }}
        sx={{ 
          mb: 2,
          '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
          '& .MuiInputBase-input': { color: 'rgba(255, 255, 255, 0.95)' },
          '& .MuiOutlinedInput-root': {
            '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
            '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.4)' },
            '&.Mui-focused fieldset': { borderColor: '#6366f1' }
          }
        }}
        onKeyPress={(e) => {
          if (e.key === 'Enter' && code.length >= 6) {
            handleVerify();
          }
        }}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleVerify}
        disabled={loading || disabled || (useBackupCode ? code.length < 8 : code.length !== 6)}
        size="large"
        sx={{
          mb: 2,
          backgroundColor: '#4f46e5',
          '&:hover': { backgroundColor: '#4338ca' },
          py: 1.5
        }}
      >
        {loading ? (
          <>
            <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
            Verifying...
          </>
        ) : (
          'Verify Code'
        )}
      </Button>

      <Box sx={{ textAlign: 'center' }}>
        <Link
          component="button"
          variant="body2"
          onClick={() => {
            setUseBackupCode(!useBackupCode);
            setCode('');
          }}
          sx={{ 
            color: '#93c5fd',
            textDecoration: 'none',
            '&:hover': { textDecoration: 'underline' }
          }}
        >
          {useBackupCode ? 'Use authenticator app code' : 'Use backup code instead'}
        </Link>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Lost your device?</strong> Use one of your backup codes to sign in.
        </Typography>
      </Alert>
    </Box>
  );
}

export default TOTPLogin;
